import { useMemo } from 'react';
import { TenantApplicationNode } from '../types/types';

export const useAnnotationIndex = (
  applicationsNodes: TenantApplicationNode[],
) => {
  return useMemo(() => {
    const nodeMap = new Map<string, TenantApplicationNode>();

    const lowercaseKeyMap = new Map<string, Map<string, string>>();

    const deploymentNodesIndex: {
      tenantId: string;
      projectId: string;
      environmentId: string;
    }[] = [];

    applicationsNodes.forEach((node) => {
      nodeMap.set(node.id, node);

      const lowercaseAnnotationsForNode = new Map<string, string>();

      node.annotations.forEach(({ key, value }) => {
        lowercaseAnnotationsForNode.set(key.toLowerCase(), value);

        const isProjectId =
          key === 'XMCloud.ProjectId' ||
          key === 'XmCloud.ProjectId' ||
          (key.toLowerCase().includes('project') &&
            key.toLowerCase().includes('id'));

        const isEnvironmentId =
          key === 'XMCloud.EnvironmentId' ||
          key === 'XmCloud.EnvironmentId' ||
          (key.toLowerCase().includes('environment') &&
            key.toLowerCase().includes('id'));

        if (isProjectId) {
          const existingEntry = deploymentNodesIndex.find(
            (entry) => entry.tenantId === node.id,
          );

          if (existingEntry) {
            existingEntry.projectId = value;
          } else {
            const envAnnotation = node.annotations.find(
              (a) =>
                a.key === 'XMCloud.EnvironmentId' ||
                a.key === 'XmCloud.EnvironmentId' ||
                (a.key.toLowerCase().includes('environment') &&
                  a.key.toLowerCase().includes('id')),
            );

            if (envAnnotation) {
              deploymentNodesIndex.push({
                tenantId: node.id,
                projectId: value,
                environmentId: envAnnotation.value,
              });
            }
          }
        }

        if (isEnvironmentId) {
          const existingEntry = deploymentNodesIndex.find(
            (entry) => entry.tenantId === node.id,
          );

          if (existingEntry) {
            existingEntry.environmentId = value;
          } else {
            const projectAnnotation = node.annotations.find(
              (a) =>
                a.key === 'XMCloud.ProjectId' ||
                a.key === 'XmCloud.ProjectId' ||
                (a.key.toLowerCase().includes('project') &&
                  a.key.toLowerCase().includes('id')),
            );

            if (projectAnnotation) {
              deploymentNodesIndex.push({
                tenantId: node.id,
                projectId: projectAnnotation.value,
                environmentId: value,
              });
            }
          }
        }
      });

      lowercaseKeyMap.set(node.id, lowercaseAnnotationsForNode);
    });

    return {
      getNode: (tenantId: string) => nodeMap.get(tenantId),

      getAnnotationCaseInsensitive: (tenantId: string, key: string) => {
        const nodeAnnotations = lowercaseKeyMap.get(tenantId);
        return nodeAnnotations?.get(key.toLowerCase());
      },

      getDeploymentInfo: (tenantId: string) => {
        const entry = deploymentNodesIndex.find((e) => e.tenantId === tenantId);
        return entry
          ? { projectId: entry.projectId, environmentId: entry.environmentId }
          : null;
      },

      getDeploymentNodes: () => deploymentNodesIndex,
    };
  }, [applicationsNodes]);
};
